import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormHeader,
  FormInput,
  FormSelect,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '../components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { selectAdvisorAdminPortalState } from '@/model/selectors';
import { postInvitationForSponsorshipsBySponsorshipIdRequest } from '@/model/advisorAdminPortalModel';
import { postCancellationForSponsorshipsBySponsorshipIdRequest } from '@client/model';

const schema = z.object({
  advisorId: z.string().min(1, 'Advisor ID is required'),
  sponsorshipId: z.string().min(1, 'Sponsorship ID is required'),
  action: z.enum(['invitation', 'cancellation'], {
    required_error: 'Please select an action',
  }),
});

type FormData = z.infer<typeof schema>;

const actionOptions = [
  { value: 'invitation', label: 'Invitation' },
  { value: 'cancellation', label: 'Cancellation' },
];

export function AdminAdvisorSponsorInviteForm() {
  const dispatch = useDispatch();
  const {
    control,
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { action: 'invitation' },
  });

  const selectedAction = watch('action');
  const { status, errorMessage } = useSelector(selectAdvisorAdminPortalState);

  const onSubmit = (data: FormData) => {
    const basePayload = {
      sponsorshipId: data.sponsorshipId,
      advisorId: data.advisorId,
    };
    if (data.action === 'invitation') {
      dispatch(
        postInvitationForSponsorshipsBySponsorshipIdRequest(basePayload),
      );
    } else {
      dispatch(
        postCancellationForSponsorshipsBySponsorshipIdRequest({
          ...basePayload,
          source: 'sponsor',
        }),
      );
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 text-gray-700"
    >
      <div className="mb-4 space-y-2">
        <FormHeader
          title="Sponsor Action"
          className="mb-0"
        />
        <p className="text-gray-600">
          Select an action, then specify advisor and sponsorship IDs.
        </p>
      </div>

      <FormSelect
        label="Action"
        id="action"
        control={control}
        name="action"
        options={actionOptions}
        placeholder="Select action"
        error={errors.action}
        required
        className="w-full"
      />

      <FormInput
        label="Advisor ID"
        id="advisorId"
        type="text"
        placeholder="Enter advisor ID"
        error={errors.advisorId}
        register={register('advisorId')}
        required
      />

      <FormInput
        label="Sponsorship ID"
        id="sponsorshipId"
        type="text"
        placeholder="Enter sponsorship ID"
        error={errors.sponsorshipId}
        register={register('sponsorshipId')}
        required
      />

      <PrimarySecondaryButtonContainer>
        <SubmitButton
          label={
            selectedAction === 'invitation'
              ? 'Send Invite'
              : 'Cancel Sponsorship'
          }
          isLoading={status === 'loading'}
        />
      </PrimarySecondaryButtonContainer>

      <FormErrorAlert
        mainErrorMessage={errorMessage ?? ''}
        className="translate-y-4"
      />
    </form>
  );
}
